import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../services/api';
import { Shield, Mail, Lock, User, Building2, AlertCircle } from 'lucide-react';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    districtName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name, formData.districtName);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.pexels.com/photos/7406300/pexels-photo-7406300.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="School building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/70 flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-12 h-12" />
              <span className="font-outfit text-3xl font-bold">RiskShield EDU</span>
            </div>
            <h1 className="font-outfit text-4xl font-bold mb-4">
              Start Your 14-Day Free Trial
            </h1>
            <p className="text-lg text-slate-200 mb-6">
              Get full access to all features including AI-powered risk assessment, training modules, and real-time monitoring.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-risk-safe flex items-center justify-center">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span>Unlimited user seats included</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-risk-safe flex items-center justify-center">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span>AI Risk Intelligence Chatbot</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-risk-safe flex items-center justify-center">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span>10 Skills Training Hub</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <Shield className="w-10 h-10 text-primary" />
            <span className="font-outfit text-2xl font-bold text-primary">RiskShield EDU</span>
          </div>

          <div className="bg-surface border border-border rounded-lg p-8">
            <h2 className="font-outfit text-2xl font-semibold text-primary mb-2">Create your account</h2>
            <p className="text-text-muted mb-6">Start building risk intelligence for your district</p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6 flex items-start gap-3" data-testid="register-error">
                <AlertCircle className="w-5 h-5 text-risk-critical flex-shrink-0 mt-0.5" />
                <span className="text-sm text-risk-critical">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                    placeholder="John Smith"
                    required
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  District Name
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="text"
                    name="districtName"
                    value={formData.districtName}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                    placeholder="Springfield School District"
                    required
                    data-testid="register-district"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                    placeholder="admin@district.edu"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                    placeholder="Min. 8 characters"
                    required
                    data-testid="register-password"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-text-muted mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                    placeholder="Confirm your password"
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white py-3 rounded-md font-medium hover:bg-primary-hover transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>

            <p className="text-center mt-6 text-text-muted">
              Already have an account?{' '}
              <Link to="/login" className="text-accent-blue hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
